/**
 * NodeEdgeService.js
 *
 * Central place for Firestore edge reads/writes.
 */

// Firebase
import {
	collection,
	doc,
	getDocs,
	onSnapshot,
	query,
	where,
	writeBatch,
} from 'firebase/firestore';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';

// Utilities
import { chunkArray } from 'utilities/Arrays';

// Constants
const MAX_BATCH_OPS = 450;

const linkNodesCallable = httpsCallable(functions, 'linkNodes');
const unlinkNodesCallable = httpsCallable(functions, 'unlinkNodes');

// Detects non-empty strings
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
const normalizeWorkspaceKey = (value) => (isNonEmptyString(value) ? value.trim() : null);

function buildAdjacentEdgeQuery(edgesRef, fieldName, nodeKey, workspaceKey = null) {
	const normalizedWorkspaceKey = normalizeWorkspaceKey(workspaceKey);
	if (normalizedWorkspaceKey) {
		return query(
			edgesRef,
			where(fieldName, '==', nodeKey),
			where('workspaceKey', '==', normalizedWorkspaceKey)
		);
	}

	return query(edgesRef, where(fieldName, '==', nodeKey));
}

/**
 * Returns canonical ordered edge endpoints for undirected semantics.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 * @returns {{ sourceNodeKey: string, targetNodeKey: string }}
 */
export function canonicalizeEdge(nodeKeyA, nodeKeyB) {
	if (!isNonEmptyString(nodeKeyA) || !isNonEmptyString(nodeKeyB)) {
		throw new Error('canonicalizeEdge: both node keys are required');
	}

	const left = nodeKeyA.trim();
	const right = nodeKeyB.trim();

	if (left === right) {
		throw new Error('canonicalizeEdge: self-edges are not allowed');
	}

	if (left < right) {
		return { sourceNodeKey: left, targetNodeKey: right };
	}

	return { sourceNodeKey: right, targetNodeKey: left };
}

/**
 * Stable deterministic edge document id.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 * @returns {string}
 */
export function buildEdgeId(nodeKeyA, nodeKeyB) {
	const { sourceNodeKey, targetNodeKey } = canonicalizeEdge(nodeKeyA, nodeKeyB);
	return `${sourceNodeKey}__${targetNodeKey}`;
}

/**
 * Creates or updates an edge document.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 * @param {Object} options
 * @param {Object} options.extraFields - Optional additional fields.
 */
export async function upsertEdge(nodeKeyA, nodeKeyB, options = {}) {
	const { workspaceKey = null } = options || {};
	const { sourceNodeKey, targetNodeKey } = canonicalizeEdge(nodeKeyA, nodeKeyB);

	const result = await linkNodesCallable({
		nodeKeyA: sourceNodeKey,
		nodeKeyB: targetNodeKey,
		workspaceKey,
	});

	return result?.data || null;
}

/**
 * Deletes a specific edge between two nodes.
 *
 * @param {string} nodeKeyA
 * @param {string} nodeKeyB
 */
export async function deleteEdge(nodeKeyA, nodeKeyB, options = {}) {
	const { workspaceKey = null } = options || {};
	const { sourceNodeKey, targetNodeKey } = canonicalizeEdge(nodeKeyA, nodeKeyB);

	await unlinkNodesCallable({
		nodeKeyA: sourceNodeKey,
		nodeKeyB: targetNodeKey,
		workspaceKey,
	});
}

/**
 * Lists adjacent node keys for a node from `_edges`.
 *
 * @param {string} nodeKey
 * @returns {Promise<string[]>}
 */
export async function listAdjacentNodeKeys(nodeKey, options = {}) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('listAdjacentNodeKeys: nodeKey is required');
	}

	const key = nodeKey.trim();
	const { workspaceKey = null } = options || {};
	const edgesRef = collection(db, collections.edges);

	const [sourceSnap, targetSnap] = await Promise.all([
		getDocs(buildAdjacentEdgeQuery(edgesRef, 'sourceNodeKey', key, workspaceKey)),
		getDocs(buildAdjacentEdgeQuery(edgesRef, 'targetNodeKey', key, workspaceKey)),
	]);

	const neighbors = new Set();

	sourceSnap.forEach((docSnap) => {
		const data = docSnap.data() || {};
		if (isNonEmptyString(data.targetNodeKey)) {
			neighbors.add(data.targetNodeKey.trim());
		}
	});

	targetSnap.forEach((docSnap) => {
		const data = docSnap.data() || {};
		if (isNonEmptyString(data.sourceNodeKey)) {
			neighbors.add(data.sourceNodeKey.trim());
		}
	});

	return Array.from(neighbors);
}

/**
 * Real-time listener for adjacent node keys.
 *
 * @param {string} nodeKey
 * @param {(neighborKeys: string[]) => void} callback
 * @returns {() => void}
 */
export function subscribeAdjacentNodeKeys(nodeKey, callback, options = {}) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('subscribeAdjacentNodeKeys: nodeKey is required');
	}
	if (typeof callback !== 'function') {
		throw new Error('subscribeAdjacentNodeKeys: callback must be a function');
	}

	const key = nodeKey.trim();
	const { workspaceKey = null } = options || {};
	const edgesRef = collection(db, collections.edges);

	let sourceRows = [];
	let targetRows = [];

	const emit = () => {
		const neighbors = new Set();

		sourceRows.forEach((data) => {
			if (isNonEmptyString(data.targetNodeKey)) {
				neighbors.add(data.targetNodeKey.trim());
			}
		});

		targetRows.forEach((data) => {
			if (isNonEmptyString(data.sourceNodeKey)) {
				neighbors.add(data.sourceNodeKey.trim());
			}
		});

		callback(Array.from(neighbors));
	};

	const unsubSource = onSnapshot(
		buildAdjacentEdgeQuery(edgesRef, 'sourceNodeKey', key, workspaceKey),
		(snapshot) => {
			sourceRows = snapshot.docs.map((docSnap) => docSnap.data() || {});
			emit();
		},
		(error) => {
			console.error('[NodeEdgeService] subscribeAdjacentNodeKeys source listener error:', error);
		}
	);

	const unsubTarget = onSnapshot(
		buildAdjacentEdgeQuery(edgesRef, 'targetNodeKey', key, workspaceKey),
		(snapshot) => {
			targetRows = snapshot.docs.map((docSnap) => docSnap.data() || {});
			emit();
		},
		(error) => {
			console.error('[NodeEdgeService] subscribeAdjacentNodeKeys target listener error:', error);
		}
	);

	return () => {
		unsubSource();
		unsubTarget();
	};
}

/**
 * Deletes all incident edges for a node.
 *
 * @param {string} nodeKey
 */
export async function deleteIncidentEdges(nodeKey) {
	if (!isNonEmptyString(nodeKey)) {
		throw new Error('deleteIncidentEdges: nodeKey is required');
	}

	const key = nodeKey.trim();
	const edgesRef = collection(db, collections.edges);

	const [sourceSnap, targetSnap] = await Promise.all([
		getDocs(query(edgesRef, where('sourceNodeKey', '==', key))),
		getDocs(query(edgesRef, where('targetNodeKey', '==', key))),
	]);

	const docIds = Array.from(
		new Set([
			...sourceSnap.docs.map((docSnap) => docSnap.id),
			...targetSnap.docs.map((docSnap) => docSnap.id),
		])
	);

	if (docIds.length === 0) return;

	const chunks = chunkArray(docIds, MAX_BATCH_OPS);
	for (const chunk of chunks) {
		const batch = writeBatch(db);
		chunk.forEach((edgeId) => {
			batch.delete(doc(db, collections.edges, edgeId));
		});
		await batch.commit();
	}
}
